import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
    getExceedingTeamRegistrantLimits,
    getHasCategoriesWithSubregistrantEnabled,
} from "@qgiv/p2p-registration";
import { useFormikContext } from "formik";
import {
    selectAllClassifications,
    selectUserSelectedClassification,
} from "../../../../redux/slices/classificationsSlice";
import { selectAllCategories } from "../../../../redux/slices/categoriesSlice";
import { selectTeams } from "../../../../redux/slices/teamsSlice";
import {
    selectAllRegistration,
    updateAddAdditionalParticipantDisabled,
} from "../../../../redux/slices/registrationSlice";
import {
    selectAllP2pSettings,
    selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
} from "../../../../redux/slices/p2pSettingsSlice";
import { selectHasParentRegistration } from "../../../../redux/slices/appSettingsSlice";
import { selectEmailVerificationInFlight } from "../../../../redux/slices/emailVerificationSlice";
import SaveAndAddParticipant from "./SaveAndAddParticipant";

const ConnectedSaveAndAddParticipant = ({ handleClick, controlId }) => {
    const { values } = useFormikContext();
    const { Team } = values;
    const classifications = useSelector(selectAllClassifications);
    const categories = useSelector(selectAllCategories);
    const teams = useSelector(selectTeams);
    const registration = useSelector(selectAllRegistration);
    const { registrations, currentRegistration } = registration;
    const p2pSettings = useSelector(selectAllP2pSettings);
    const { enableClassifications, additionalRegistrants } = p2pSettings;
    const allowSubRegistrantToChangeRegRole = useSelector(
        selectAllowSubRegistrantToChangeRegRoleBasedOnEnabledTeams,
    );
    const hasParentRegistration = useSelector(selectHasParentRegistration);

    const hasClassifications =
        enableClassifications && classifications.length > 0;

    const { hasSelectedClassification, selectedClassification } = useSelector(
        selectUserSelectedClassification,
    );

    const emailVerificationInFlight = useSelector(
        selectEmailVerificationInFlight,
    );

    // Add Participant button should be hidden when:
    // 1. additionalRegistrants is set to false

    // Add Participant button should be visible but disabled when:
    // 1. The selected category's attachSubregistrants setting is true, but no available
    //    categories based on category level settings or race conditions
    // 2. The event level setting of allowSubRegistrantsToChangeRegRole is false AND
    //      a. primary registrant creates a new team AND
    //      b. attachTeamMembers is false on all available categories for additional reg OR
    //      c. enableTeamMembers is false on the event level settings
    // 3. Classification registrant limits have been met
    // 4. Classification team registrant limit has been met and
    //    allowSubRegistrantsToChangeRegRole is disabled
    // 5. Email verification api call is in flight

    const getExceedingClassificationLimits = (_selectedClassification) => {
        // Since registrations append empty objects for new registrants
        // it's length will be valid for accessing classification registrant limits
        const { registrantSpotsLeft } = _selectedClassification;
        const totalRegistrations = registrations.length;
        if (
            registrantSpotsLeft !== -1 &&
            totalRegistrations >= registrantSpotsLeft
        ) {
            return true;
        }
        return false;
    };

    // If all categories are either only Team Captain or don't allow Subregistrants then we will not
    // have any Categories for the Additional Reg to selectAllCategories.  In this case we should
    // hide the button
    const hasCategoriesWithSubregistrantsEnabled =
        getHasCategoriesWithSubregistrantEnabled({
            categories,
            hasSelectedClassification,
            selectedClassification,
        });

    const hasReachedMaxCapOf30RegistrantsAtOneTime = registrations.length >= 30;

    // We also need to set a message when disabling the add part btn
    let disabledToolTipContext = "";
    const setAddPartBtnDisable = () => {
        if (emailVerificationInFlight) {
            // we don't need a tooltip for email verification in flight
            return true;
        }
        // Handles the edge case where an org has Additional enabled at event level
        // but does not have any Categories that are enabled for Additional reg
        // The button will be disabled to prevent user from ending up on a broken form
        if (!hasCategoriesWithSubregistrantsEnabled) {
            disabledToolTipContext =
                "There are no registration options available for additional participants.";
            return true;
        }

        // Handle max cap for registrations at one time. Once we are on the 30th registration
        // we need to disable with a tooltip. The submission url is too long to progress over 30, and
        // the BE has determined 30 and less should be safe.
        if (hasReachedMaxCapOf30RegistrantsAtOneTime) {
            disabledToolTipContext =
                "You've reached the max number of participants (30) that may be registered at once. You can add any remaining participants to this registration from your Fundraising Dashboard, or you can create a second registration.";
            return true;
        }

        // Classification Registrant Limit
        if (hasSelectedClassification) {
            // returns true if number of registrations is >= the selected classifications registrantSpotsLeft
            const classificationRegistrantLimitMet =
                getExceedingClassificationLimits(selectedClassification);

            const { title = "this classification" } = selectedClassification;

            if (classificationRegistrantLimitMet) {
                disabledToolTipContext = `There are no more available spots for ${title}.`;
                return classificationRegistrantLimitMet;
            }
        }
        // Team Registrant Limit, lives outside of classification check since they inherit their limits from the
        // classification they are created under
        if (Team) {
            const teamRegistrantLimitMet = getExceedingTeamRegistrantLimits({
                teams,
                registrations,
                currentRegistration,
                Team,
                // pre select is false sense team is already selected for this registrant
                preSelect: false,
            });
            if (teamRegistrantLimitMet && !allowSubRegistrantToChangeRegRole) {
                disabledToolTipContext =
                    "There are no more available spots on the team.";
                return true;
            }
        }

        disabledToolTipContext = "";
        return false;
    };

    const additionalParticipantsDisabled = setAddPartBtnDisable();

    const displayAddAdditionalRegistrant =
        additionalRegistrants &&
        hasCategoriesWithSubregistrantsEnabled &&
        !hasParentRegistration;

    const saveAndAddParticipantProps = {
        additionalParticipantsDisabled,
        displayAddAdditionalRegistrant,
        disabledToolTipContext,
        handleClick,
        hasClassifications,
        controlId,
        updateAddAdditionalParticipantDisabled,
    };
    return <SaveAndAddParticipant {...saveAndAddParticipantProps} />;
};

ConnectedSaveAndAddParticipant.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    handleClick: PropTypes.func.isRequired,
};

export default ConnectedSaveAndAddParticipant;
